import type { AppAction, AppState, ParsedRoute, SceneMetadata } from './types.js';

const isDev = (() => {
  try {
    return process.env['NODE_ENV'] !== 'production';
  } catch {
    return true;
  }
})();

function warnInvalid(state: AppState, action: AppAction): void {
  if (!isDev) return;
  console.warn(
    `[state] invalid transition ignored: action ${action.type} not allowed in state ${state.kind}`,
  );
}

function routeFromShare(result: { kind: string }): ParsedRoute | null {
  if (result.kind === 'main' || result.kind === 'embed') {
    const r = result as {
      kind: 'main' | 'embed';
      modelId: string;
      cameraEncoded: string | null;
      variantOverride: 'rad' | 'sd' | 'hd' | 'source' | null;
      viewboxIndex: number | null;
    };
    return {
      modelId: r.modelId,
      embed: r.kind === 'embed',
      cameraEncoded: r.cameraEncoded,
      // Punto 2 SOG full SH 2026-06-08 (Codex iter1 HIGH #1 fix).
      variantOverride: r.variantOverride,
      viewboxIndex: r.viewboxIndex,
    };
  }
  return null;
}

function isCompleteMetadata(partial: Partial<SceneMetadata>): partial is SceneMetadata {
  return 'hotspots' in partial && 'presets' in partial && 'floorplan' in partial;
}

// Pure transition function. Invalid transitions return current state unchanged.
// All side effects (fetch, dispose, render) live in the orchestrator (4.C).
export function transition(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'RESET':
      return { kind: 'idle' };

    case 'PARSE_URL':
      return { kind: 'parsing-url' };

    case 'URL_PARSED': {
      const { result } = action;
      if (result.kind === 'upload-fallback') return { kind: 'upload-fallback' };
      if (result.kind === 'invalid') return { kind: 'error-not-found', route: null };
      const route = routeFromShare(result);
      if (route === null) {
        warnInvalid(state, action);
        return state;
      }
      return { kind: 'fetching-meta', route };
    }

    case 'META_FETCHED': {
      if (state.kind !== 'fetching-meta' && state.kind !== 'error-transient') {
        warnInvalid(state, action);
        return state;
      }
      return {
        kind: 'loading-scene',
        route: action.route,
        meta: action.meta,
        modelProgress: 0,
        metadata: {},
      };
    }

    case 'MODEL_PROGRESS': {
      if (state.kind !== 'loading-scene') {
        warnInvalid(state, action);
        return state;
      }
      // total <= 0 = size unknown / indeterminate. Preserve current progress
      // instead of clamping to 1, which would falsely show 100% complete
      // (UI in 4.D shows an indeterminate spinner when modelProgress stays 0).
      if (action.total <= 0) {
        return state;
      }
      const ratio = Math.min(1, Math.max(0, action.loaded / action.total));
      return { ...state, modelProgress: ratio };
    }

    case 'METADATA_PARTIAL': {
      if (state.kind !== 'loading-scene' && state.kind !== 'loaded') {
        warnInvalid(state, action);
        return state;
      }
      if (state.kind === 'loaded') {
        const merged = { ...state.metadata, [action.key]: action.value } as SceneMetadata;
        return { ...state, metadata: merged };
      }
      const mergedPartial: Partial<SceneMetadata> = {
        ...state.metadata,
        [action.key]: action.value,
      };
      return { ...state, metadata: mergedPartial };
    }

    case 'SCENE_LOADED': {
      if (state.kind !== 'loading-scene') {
        warnInvalid(state, action);
        return state;
      }
      if (!isCompleteMetadata(state.metadata)) {
        warnInvalid(state, action);
        return state;
      }
      return {
        kind: 'loaded',
        route: state.route,
        meta: state.meta,
        metadata: state.metadata,
      };
    }

    case 'ERROR_EXPIRED':
      return { kind: 'error-expired', route: action.route, expiresAt: action.expiresAt };

    case 'ERROR_NOT_FOUND':
      return { kind: 'error-not-found', route: action.route };

    case 'ERROR_TRANSIENT':
      return {
        kind: 'error-transient',
        route: action.route,
        reason: action.reason,
        canRetry: action.canRetry,
        attempt: action.attempt,
        lastFailureAt: Date.now(),
      };

    case 'RETRY': {
      // Step 7 Phase 0: RETRY also valid from cancelled-by-user (user
      // changed mind after declining the mobile guardrail) — re-enters
      // fetching-meta to refresh `expires_at` + meta before re-asking.
      if (state.kind === 'cancelled-by-user') {
        return { kind: 'fetching-meta', route: state.route };
      }
      if (state.kind !== 'error-transient') {
        warnInvalid(state, action);
        return state;
      }
      if (!state.canRetry || state.route === null) {
        warnInvalid(state, action);
        return state;
      }
      return { kind: 'fetching-meta', route: state.route };
    }

    case 'LOAD_CANCELLED_BY_USER': {
      // Step 7 Phase 0: only valid when waiting for the user response
      // after a successful meta fetch (state still in fetching-meta).
      if (state.kind !== 'fetching-meta') {
        warnInvalid(state, action);
        return state;
      }
      return { kind: 'cancelled-by-user', route: action.route, meta: action.meta };
    }

    default:
      warnInvalid(state, action satisfies never);
      return state;
  }
}
